import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { queryKeys } from '~/api/queryKeys';
import { jitteredPollMs } from '~/utils/jitter';

import {
  FUNNEL_SUMMARY_POLL_MS,
  FUNNEL_SUMMARY_SAFETY_POLL_MS,
  progressSignature,
} from '../constants/polling';
import {
  getFunnelComparison,
  getFunnelProducts,
  getFunnelPublish,
  getFunnelStageDetail,
  getFunnelStageReasons,
  getFunnelSummary,
  getFunnelTimeline,
  getFunnelVerify,
} from '../services/run-funnel.service';
import type { FunnelProductMatrixParams } from '../types/run-funnel.api.types';
import { isRunLive } from '../utils/run-status';

import { useRunProgress } from './use-run-progress';

const ACTIVE_VERDICT_STATES = new Set(['RUNNING', 'WAITING']);

/**
 * ① The summary funnel: verdict, answer cards, funnel line, waterfall. Run progress is the ONE
 * clock per open batch page: while it polls, the summary is re-read ~5 s after progress MOVED
 * (its signature changed — the summary is built from the same snapshot), and otherwise only by a
 * slow ~60 s safety re-read (gate outcomes / rejections the signature may miss), open gate
 * included. Once progress stops (terminal run, e.g. delivery still settling) or
 * has no answer, the summary polls itself ~5 s while its verdict is RUNNING/WAITING, as before.
 * The caller reads `data.kind` — `'unavailable'` means render today's `RunProgressCard` unchanged
 * (resilience rule §10 step B); any other failure is a real error (retry; nothing else blocked).
 */
export function useFunnelSummary(runRef: string) {
  // Same cache entry the run shell already polls — no extra request.
  const progressQuery = useRunProgress(runRef);
  const progress = progressQuery.data;
  const progressDrives = !!progress && isRunLive(progress.runStatus);
  const signature = useMemo(() => progressSignature(progress), [progress]);

  // When progress last moved: the `dataUpdatedAt` of the answer that changed its signature. The
  // first answer for a run is only the baseline (0) — the summary's own mount read covers it.
  // Render-phase update (no effect round-trip), the same pattern as `useProductHistory`'s reset.
  const [moved, setMoved] = useState({ runRef, signature, at: 0 });
  if (moved.runRef !== runRef) {
    setMoved({ runRef, signature, at: 0 });
  } else if (signature !== null && moved.signature !== signature) {
    setMoved({
      runRef,
      signature,
      at: moved.signature === null ? 0 : progressQuery.dataUpdatedAt,
    });
  }
  const movedAt = moved.runRef === runRef ? moved.at : 0;

  return useQuery({
    queryKey: queryKeys.catalogService.funnelSummary(runRef),
    queryFn: () => getFunnelSummary(runRef),
    enabled: !!runRef,
    refetchInterval: (query) => {
      const result = query.state.data;
      // 'unavailable' (route not deployed) and errored-without-data never poll, as before.
      if (result?.kind !== 'ok') return false;
      // Progress polls: re-read soon while this answer predates progress's last move (a decision
      // that invalidated both already answered after it, so it costs no second read); otherwise
      // a slow safety re-read catches ledger gate outcomes the progress signature does not carry.
      if (progressDrives) {
        return jitteredPollMs(
          query,
          query.state.dataUpdatedAt < movedAt
            ? FUNNEL_SUMMARY_POLL_MS
            : FUNNEL_SUMMARY_SAFETY_POLL_MS,
        );
      }
      return ACTIVE_VERDICT_STATES.has(result.summary.verdict.state)
        ? jitteredPollMs(query, FUNNEL_SUMMARY_POLL_MS)
        : false;
    },
    refetchIntervalInBackground: false,
  });
}

/** ② One stage's drop/fail reasons (panel Why) — read only while that tab is open. */
export function useFunnelStageReasons(
  runRef: string,
  stage: string | null,
  attempt: number | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.catalogService.funnelStageReasons(runRef, stage ?? '', attempt),
    queryFn: () => getFunnelStageReasons(runRef, stage ?? '', attempt),
    enabled: enabled && !!runRef && !!stage,
  });
}

/** ④ Publish/live counts — lazy on open, cached once read. */
export function useFunnelPublish(runRef: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogService.funnelPublish(runRef),
    queryFn: () => getFunnelPublish(runRef),
    enabled: enabled && !!runRef,
    staleTime: 60_000,
  });
}

/** ⑤ Stored-vs-live cross-check — lazy on open, cached once read. */
export function useFunnelVerify(runRef: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogService.funnelVerify(runRef),
    queryFn: () => getFunnelVerify(runRef),
    enabled: enabled && !!runRef,
    staleTime: 60_000,
  });
}

/** ⑥ "Vs last batch" — lazy on open, cached once read; hidden by the caller until CS-5 ships. */
export function useFunnelComparison(runRef: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogService.funnelComparison(runRef),
    queryFn: () => getFunnelComparison(runRef),
    enabled: enabled && !!runRef,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * ⑦ The stage page's full breakdown — read only while that page is open for that stage. `data`
 * is `null` once resolved on a deploy that hasn't shipped the route yet (service-level degrade);
 * the stage page then falls back to ①'s own stage fields, never a blank/error page.
 */
export function useFunnelStageDetail(
  runRef: string,
  stage: string | null,
  attempt: number | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.catalogService.funnelStageDetail(runRef, stage ?? '', attempt),
    queryFn: () => getFunnelStageDetail(runRef, stage ?? '', attempt),
    enabled: enabled && !!runRef && !!stage,
    staleTime: 30_000,
  });
}

/** ⑧ The stage page's Timeline tab — read only while that tab is open; `null` degrades to the
 *  tab's existing stage-attempts fallback. */
export function useFunnelTimeline(
  runRef: string,
  stage: string | null,
  limit: number,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.catalogService.funnelTimeline(runRef, stage, limit),
    queryFn: () => getFunnelTimeline(runRef, stage, limit),
    enabled: enabled && !!runRef,
    staleTime: 30_000,
  });
}

/** ⑨ The mix's Products matrix — read only while the Products section is visible, one query per
 *  filter set; `null` degrades to the section's existing products-source links. */
export function useFunnelProducts(
  runRef: string,
  params: FunnelProductMatrixParams,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.catalogService.funnelProducts(runRef, { ...params }),
    queryFn: () => getFunnelProducts(runRef, params),
    enabled: enabled && !!runRef,
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}
